import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { CloseIcon } from "../../../minimals/theme/overrides/CustomIcons";
import axios from "axios";
import { BasicTask, ConcatenatedDocumentTypes } from "../types";
import { generateFullPaths } from "./generateFullPaths";
import { UploadMultiFile } from "../../components/upload";
import { stripDataUriPrefix } from "../../utils/stripDataUriPrefix";
import { toBase64 } from "../../utils/helpers";
import OrderAttachments from "../../shared/OrderAttachments";
import { Attachment } from "../../shared/types/Attachment";

export default function DocumentUploadModal({
  selectedTask,
  setSelectedTask,
  updateTasks,
  orderNumber
}: {
  selectedTask: BasicTask | null;
  setSelectedTask: (value: any) => void;
  updateTasks: () => void;
  orderNumber: string | null;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [concatenatedDocumentTypes, setConcatenatedDocumentTypes] = useState<ConcatenatedDocumentTypes[]>([]);
  const [selectedConcatenatedDocumentType, setSelectedConcatenatedDocumentType] =
    useState<ConcatenatedDocumentTypes | null>(null);
  const [additionalDescription, setAdditionalDescription] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [selectedAttachments, setSelectedAttachments] = useState<Attachment[]>([]);

  const closeModal = () => {
    setSelectedTask(null);
  };

  const open = !!selectedTask;

  const getAndSetDocumentTypes = async () => {
    const { data } = await axios.get(`/api/taskmanagement/documents/getcategories?ordernumber=${orderNumber}`);

    const concatenatedDocumentTypes = generateFullPaths(data);
    setConcatenatedDocumentTypes(concatenatedDocumentTypes);
  };

  const handleFileDrop = useCallback(
    (acceptedFile: any) => {
      const file = Object.assign(acceptedFile[0], {
        preview: URL.createObjectURL(acceptedFile[0])
      });
      setFile(file);
    },
    [setFile]
  );

  const save = async () => {
    setSubmitting(true);
    if (selectedConcatenatedDocumentType && file) {
      await saveNewAttachment();
    }
    if (selectedAttachments.length > 0) {
      await saveMomentumAttachment();
    }
    setSubmitting(false);
    updateTasks();
    closeModal();
  };

  const saveNewAttachment = async () => {
    await axios.post("/api/taskmanagement/documents/upload", {
      taskId: selectedTask?.id,
      documentBase64: stripDataUriPrefix(await toBase64(file as File)),
      fileName: file?.name,
      subcategoryId: selectedConcatenatedDocumentType?.subcategoryId,
      description: selectedConcatenatedDocumentType?.description,
      descriptionAdditional: additionalDescription
    });
  };

  const saveMomentumAttachment = async () => {
    const momentumDocumentIds = selectedAttachments.map((sa) => sa.id);
    await axios.post("/api/taskmanagement/documents/addMomentumDocumentsToTask", {
      taskId: selectedTask?.id,
      momentumDocumentIds
    });
  };

  useEffect(() => {
    getAndSetDocumentTypes();
  }, []);

  return (
    <Dialog open={open} onClose={closeModal} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack direction="column">
          <Typography variant="subtitle1">Add Associated Document</Typography>
          <Typography variant="body2">{selectedTask?.description}</Typography>
        </Stack>
      </DialogTitle>
      <IconButton
        onClick={closeModal}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500]
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ paddingBottom: 3 }}>
        <Stack direction="row" spacing={2} paddingTop={1}>
          <Card sx={{ width: "100%" }}>
            <CardContent>
              <Stack direction="column" justifyContent="space-between" spacing={2}>
                <Autocomplete
                  fullWidth
                  onChange={(e, value) => setSelectedConcatenatedDocumentType(value)}
                  options={concatenatedDocumentTypes}
                  getOptionLabel={(option) => option.fullPath || ""}
                  renderInput={(params) => (
                    <TextField {...params} label="Document Type" placeholder="Start typing..." />
                  )}
                />
                <TextField label="Additional Description" onChange={(e) => setAdditionalDescription(e.target.value)} />
                <UploadMultiFile
                  files={file ? [file] : []}
                  onDrop={handleFileDrop}
                  onRemove={() => setFile(null)}
                  onRemoveAll={() => setFile(null)}
                  showPreview={false}
                  showSelectedFiles={true}
                  multiple={false}
                />
                {selectedConcatenatedDocumentType && (
                  <Stack direction="column" justifyContent="right">
                    <Box>
                      <Typography variant="body2">Uploaded document will be saved to</Typography>
                      <Typography variant="subtitle2">{`${selectedConcatenatedDocumentType.fullPath} ${
                        additionalDescription ? `- ${additionalDescription}` : ""
                      }`}</Typography>
                    </Box>
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>
          <Card sx={{ width: "100%" }}>
            <CardContent>
              <Typography variant="body2" marginBottom={1}>
                Or <strong>Choose a document from Momentum</strong>
              </Typography>
              <OrderAttachments
                selectedAttachments={selectedAttachments}
                setSelectedAttachments={setSelectedAttachments}
                orderNumber={selectedTask?.orderNumber}
              />
            </CardContent>
          </Card>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={closeModal} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={(!selectedConcatenatedDocumentType || !file) && selectedAttachments.length === 0}
          onClick={save}
        >
          {submitting ? "Saving...." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
